import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import WorkspacesIcon from '@mui/icons-material/Workspaces';

import {
  Checkbox,
  Divider,
  IconButton,
  Input,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";

import "./EmailList.css";
import AssignAgentModal from "../Chat/AssignAgentModal";
import Loader from "../Components/Loader";
import WarningDialog from "../Components/WarningDialog";
import formatTime from "../Helper/formatTime";
import SortAgentDown from "../Images/CustomAgentDown.svg";
import SortAgentUp from "../Images/CustomAgentUp.svg";
import SortContactDown from "../Images/CustomContactDown.svg";
import SortContactUp from "../Images/CustomContactUp.svg";
import SortDateDown from "../Images/CustomDateDown.svg";
import SortDateUp from "../Images/CustomDateUp.svg";
import { assignEmail, getEmailList } from "../Store/Actions/TicketAction";
import { UPDATE_EMAIL } from "../Store/Types/ChatTypes";

const EmailList = ({
  activeTab,
  selectedEmail,
  setActiveTab,
  setSelectedEmail,
  setIsBack,
  isAssigningEmails,
  setIsAssigningEmails,
}) => {
  const { myInfo } = useSelector((state) => state.auth);
  const level = myInfo.user.Level;
  const [agentToAssign, setAgentToAssign] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailsToAssign, setEmailsToAssign] = useState([]);
  const [sortDir, setSortDir] = useState("desc");
  const [isAssignAgentModalShown, setIsAssignAgentModalShown] = useState(false);

  const [isAssignAgentWarningShown, setIsAssignAgentWarningShown] =
    useState(false);

  const [isAssigning, setIsAssigning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [status, setStatus] = useState(level > 0 ? -2 : 1); // level 0: assigned, else: all unresolved
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null); // For each email tab counts
  const dispatch = useDispatch();
  const { updateEmail } = useSelector((state) => state.chat);
  const open = Boolean(anchorEl);

  const chatTypeList =
    level > 0
      ? [
          {
            key: "All",
            name: "All",
            load: "all",
            icon: "material-symbols:assignment-outline-rounded",
            status: -2,
          },

          {
            key: "Assigned",
            name: "Assigned",
            load: "assigned",
            icon: "material-symbols:assignment-ind-outline",
            status: 1,
          },

          {
            key: "Queued",
            name: "Queued",
            load: "unassigned",
            icon: "material-symbols:assignment-late-outline",
            status: -1,
          },

          {
            key: "InProgress",
            name: "In Progress",
            load: "inprogress",
            icon: "material-symbols:assignment-returned-outline-rounded",
            status: 2,
          },

          {
            key: "Resolved",
            name: "Resolved",
            load: "resolved",
            icon: "material-symbols:assignment-turned-in-outline-rounded",
            status: 0,
          },
        ]
      : [
          {
            key: "Assigned",
            name: "Assigned",
            load: "assigned",
            icon: "material-symbols:assignment-ind-outline",
            status: 1,
          },

          {
            key: "Queued",
            name: "Queued",
            load: "unassigned",
            icon: "material-symbols:assignment-late-outline",
            status: -1,
          },

          {
            key: "InProgress",
            name: "In Progress",
            load: "inprogress",
            icon: "material-symbols:assignment-returned-outline-rounded",
            status: 2,
          },

          {
            key: "Resolved",
            name: "Resolved",
            load: "resolved",
            icon: "material-symbols:assignment-turned-in-outline-rounded",
            status: 0,
          },
        ];

  useEffect(() => {
    if (emails) {
      fetchEmailList(true, status, searchValue, sortBy, sortDir, 1, true);
    } else {
      // Run loader for first load
      fetchEmailList(true, status);
    }
  }, [updateEmail]);

  const fetchEmailList = (
    isFirstLoad,
    status,
    search,
    sortBy,
    sortDir,
    page,
    isUpdateEmail // To check if called from effect
  ) => {
    if (isFirstLoad) {
      !isUpdateEmail && setIsLoading(true);
      setPage(1);
    }

    getEmailList(status, search, sortBy, sortDir, page)
      .then((response) => {
        if (response.status) {
          isFirstLoad
            ? setEmails(response.data.chats ? response.data.chats : [])
            : setEmails([...emails, ...response.data.chats]);

          setTotal(response.data.total);
          setCounts(response.data.counts);
          setIsLoading(false);
        } else {
          console.log("error", response.error);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderLastEmail = (message) => {
    let retval;
    const limit = 50;

    if (String(message).length > limit) {
      const moreText = message.substring(0, limit) + "...";
      retval = moreText;
    } else {
      retval = message;
    }

    // return <span className="last-message-contents">{retval}</span>;
    return retval;
  };

  const renderSortingIcon = () => {
    if (sortDir === "asc" && sortBy === "date") {
      return SortDateUp;
    } else if (sortDir === "asc" && sortBy === "name") {
      return SortContactUp;
    } else if (sortDir === "asc" && sortBy === "agent") {
      return SortAgentUp;
    } else if (sortDir === "desc" && sortBy === "date") {
      return SortDateDown;
    } else if (sortDir === "desc" && sortBy === "name") {
      return SortContactDown;
    } else if (sortDir === "desc" && sortBy === "agent") {
      return SortAgentDown;
    }
  };

  const handleSelectedEmail = (email) => {
    setSelectedEmail(email);
    setIsBack(false);

    /* // Used on socket event, for notification
    localStorage.setItem("selectedChatID", email.ChatListID);

    // Refresh chat list and message list
    dispatch({ type: UPDATE_CHAT }); */
  };

  function assignAgent() {
    setIsAssigning(true);

    assignEmail({
      agentid: agentToAssign.AgentID,
      assignby: myInfo.user.AgentID,
      ticketid: emailsToAssign?.map((emailToAssign) => emailToAssign.CaseID),
    })
      .then((response) => {
        if (response.status) {
          toast.success("Emails assigned successfully", { autoClose: 3000 });
          setIsAssigning(false);
          setIsAssignAgentWarningShown(false);
          setIsAssignAgentModalShown(false);
          setAgentToAssign(null);
          setEmailsToAssign([]);
          setIsAssigningEmails(false);
          dispatch({ type: UPDATE_EMAIL });
        } else {
          console.log("error", response.error);
          toast.error("Error assigning emails: " + response.error, { autoClose: 3000 });
          setIsAssigning(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Error assigning emails: " + err, { autoClose: 3000 });
        setIsAssigning(false);
      });
  }

  return (
    <>
      {isAssigningEmails ? (
        <div className="selected-emails-to-assign">
          <Tooltip arrow placement="bottom" title="Cancel">
            <IconButton
              onClick={() => {
                setEmailsToAssign([]);
                setIsAssigningEmails(false);
              }}
            >
              <Icon height="28" icon="tabler:x" />
            </IconButton>
          </Tooltip>

          <p>{emailsToAssign.length} Emails Selected</p>

          <Tooltip arrow placement="bottom" title="Assign agent">
            <IconButton
              disabled={emailsToAssign.length === 0}
              onClick={() => setIsAssignAgentModalShown(true)}
            >
              <Icon height="28" icon="material-symbols:person-add" />
            </IconButton>
          </Tooltip>
        </div>
      ) : (
        <Tabs
          className="tabs"
          TabIndicatorProps={{
            style: {
              height: "2px",
              backgroundColor: "#808080",
            },
          }}
          value={activeTab}
          variant="fullWidth"
        >
          {chatTypeList.map((item, index) => (
            <Tooltip arrow key={item.status} placement="top" title={item.name}>
              <Tab
                icon={<Icon height="24" icon={item.icon} />}
                label={counts ? counts[item.load] : null}
                onClick={() => {
                  setActiveTab(index);
                  setStatus(item.status);
                  fetchEmailList(
                    true,
                    item.status,
                    searchValue,
                    sortBy,
                    sortDir
                  );
                }}
              />
            </Tooltip>
          ))}
        </Tabs>
      )}

      <div className="list_search">
        {/* <div className="single_button">
          <Tooltip title="Filter list" arrow>
            <IconButton onClick={handleClickFilterChannel} size="small">
              {selectedChannel === "waba" ? (
                <img alt="" src={WhatsappBusinessIcon} height={20} />
              ) : (
                <Icon
                  height="20"
                  icon={
                    selectedChannel === "all"
                      ? "fluent:bubble-multiple-20-filled"
                      : selectedChannel === "wa"
                      ? "logos:whatsapp-icon"
                      : selectedChannel === "fbchat"
                      ? "logos:facebook"
                      : selectedChannel === "ig"
                      ? "skill-icons:instagram"
                      : selectedChannel === "livechat"
                      ? "token:chat"
                      : ""
                  }
                />
              )}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorElFilterChannel}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            id="Filter_menu"
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={openFilterChannel}
            onClose={handleCloseFilterChannel}
            onClick={handleCloseFilterChannel}
          >
            <MenuItem
              selected={selectedChannel === "all"}
              onClick={() => {
                setSelectedChannel("all");

                fetchChatList(
                  true,
                  "all",
                  status,
                  selectedWABAAccountID,
                  searchValue,
                  sortBy,
                  sortDir
                );
              }}
            >
              <ListItemIcon>
                <Icon height="26" icon="fluent:bubble-multiple-20-filled" />
              </ListItemIcon>
              All
            </MenuItem>

            <MenuItem
              selected={selectedChannel === "wa"}
              onClick={() => {
                setSelectedChannel("wa");

                fetchChatList(
                  true,
                  "wa",
                  status,
                  selectedWABAAccountID,
                  searchValue,
                  sortBy,
                  sortDir
                );
              }}
            >
              <ListItemIcon>
                <Icon height="26" icon="logos:whatsapp-icon" />
              </ListItemIcon>
              WhatsApp
            </MenuItem>

            {channels.waba?.map((channel) => (
              <MenuItem
                key={channel.ID}
                selected={selectedChannel === "waba"}
                onClick={() => {
                  setSelectedChannel("waba");
                  setSelectedWABAAccountID(channel.ID);

                  fetchChatList(
                    true,
                    "waba",
                    status,
                    channel.ID,
                    searchValue,
                    sortBy,
                    sortDir
                  );
                }}
              >
                <ListItemIcon>
                  <img
                    alt=""
                    src={WhatsappBusinessIcon}
                    height={26}
                    style={{ marginRight: "18px" }}
                  />
                </ListItemIcon>
                {channel.Name}
              </MenuItem>
            ))}

            {channels.facebook?.map((channel) => (
              <MenuItem
                key={channel.FacebookID}
                selected={selectedChannel === "fbchat"}
                onClick={() => {
                  setSelectedChannel("fbchat");

                  fetchChatList(
                    true,
                    "fbchat",
                    status,
                    selectedWABAAccountID,
                    searchValue,
                    sortBy,
                    sortDir
                  );
                }}
              >
                <ListItemIcon>
                  <Icon height="26" icon="logos:facebook" />
                </ListItemIcon>
                {channel.FacebookName}
              </MenuItem>
            ))}

            {channels.instagram?.map((channel) => (
              <MenuItem
                key={channel.InstagramID}
                selected={selectedChannel === "ig"}
                onClick={() => {
                  setSelectedChannel("ig");

                  fetchChatList(
                    true,
                    "ig",
                    status,
                    selectedWABAAccountID,
                    searchValue,
                    sortBy,
                    sortDir
                  );
                }}
              >
                <ListItemIcon>
                  <Icon height="26" icon="skill-icons:instagram" />
                </ListItemIcon>
                {channel.InstagramName}
              </MenuItem>
            ))}

            <MenuItem
              selected={selectedChannel === "livechat"}
              onClick={() => {
                setSelectedChannel("livechat");

                fetchChatList(
                  true,
                  "livechat",
                  status,
                  selectedWABAAccountID,
                  searchValue,
                  sortBy,
                  sortDir
                );
              }}
            >
              <ListItemIcon>
                <Icon height="26" icon="token:chat" />
              </ListItemIcon>
              Live Chat
            </MenuItem>
          </Menu>
        </div> */}

        <div className="searchbar_container">
          <SearchOutlined className="search_icon" />

          <Input
            className="searchbar"
            placeholder="Search..."
            disableUnderline={true}
            type="text"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              fetchEmailList(true, status, searchValue, sortBy, sortDir)
            }
          />

          {searchValue && (
            <CloseOutlinedIcon
              className="clear_search_icon"
              onClick={() => {
                setSearchValue("");
                fetchEmailList(true, status, "", sortBy, sortDir);
              }}
            />
          )}
        </div>

        <div
          className="single_button"
          style={{ border: "none", boxShadow: "none" }}
        >
          <Tooltip title="Sort list" arrow>
            <IconButton onClick={handleClick} size="small">
              {sortBy === "department" ? (
                <WorkspacesIcon />
              ) : (
                <img alt="" src={renderSortingIcon()} height={26} />
              )}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            id="Filter_menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                setSortDir("asc");
                fetchEmailList(true, status, searchValue, sortBy, "asc");
              }}
              selected={sortDir === "asc"}
            >
              <ListItemIcon>
                <ArrowDropUpIcon />
              </ListItemIcon>
              Ascending
            </MenuItem>

            <MenuItem
              onClick={() => {
                setSortDir("desc");
                fetchEmailList(true, status, searchValue, sortBy, "desc");
              }}
              selected={sortDir === "desc"}
            >
              <ListItemIcon>
                <ArrowDropDownIcon />
              </ListItemIcon>
              Descending
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                setSortBy("date");
                fetchEmailList(true, status, searchValue, "date", sortDir);
              }}
              selected={sortBy === "date"}
            >
              <ListItemIcon>
                <CalendarMonthIcon />
              </ListItemIcon>
              By Date
            </MenuItem>

            <MenuItem
              onClick={() => {
                setSortBy("name");
                fetchEmailList(true, status, searchValue, "name", sortDir);
              }}
              selected={sortBy === "name"}
            >
              <ListItemIcon>
                <BadgeIcon />
              </ListItemIcon>
              By Name
            </MenuItem>

            {/* {level > 0 ? ( */}
            <MenuItem
              onClick={() => {
                setSortBy("agent");
                fetchEmailList(true, status, searchValue, "agent", sortDir);
              }}
              selected={sortBy === "agent"}
            >
              <ListItemIcon>
                <SupportAgentIcon />
              </ListItemIcon>
              By Agent
            </MenuItem>
            {/* )} */}

            <MenuItem
              onClick={() => {
                setSortBy("department");
                fetchEmailList(
                  true,
                  status,
                  searchValue,
                  "department",
                  sortDir
                );
              }}
              selected={sortBy === "department"}
            >
              <ListItemIcon>
                <WorkspacesIcon />
              </ListItemIcon>
              By Department
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="email-list" id="infiniteScrollTarget">
        {isLoading ? (
          <Loader />
        ) : (
          <InfiniteScroll
            className="infinite-scroll"
            dataLength={emails.length}
            endMessage={
              <div className="end-message">
                <b>End of list</b>
              </div>
            }
            hasMore={emails.length < total}
            loader={<Loader />}
            next={() => {
              fetchEmailList(
                false,
                status,
                searchValue,
                sortBy,
                sortDir,
                page + 1
              );

              setPage(page + 1);
            }}
            scrollableTarget="infiniteScrollTarget"
            scrollThreshold={0.95}
          >
            {emails.length > 0 ? (
              emails.map((email) => (
                <div
                  className={`email-list-item ${
                    email.CaseID === selectedEmail?.CaseID ? "selected" : ""
                  }`}
                  key={email.CaseID}
                  onClick={() => {
                    handleSelectedEmail(email);
                  }}
                >
                  {isAssigningEmails && (
                    <Checkbox
                      checked={emailsToAssign.some(
                        (emailToAssign) => emailToAssign.CaseID === email.CaseID
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Add
                          setEmailsToAssign([...emailsToAssign, email]);
                        } else {
                          // Remove
                          setEmailsToAssign(
                            emailsToAssign.filter(
                              (emailToAssign) =>
                                emailToAssign.CaseID !== email.CaseID
                            )
                          );
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  <div className="email-list-item-contents">
                    <div className="email-info">
                      <div className="contact-info">
                        <span className="contact-name">
                          {email.CustomerName
                            ? email.CustomerName
                            : "Unknown Contact"}
                        </span>
                      </div>

                      <span className="account-info">{email.Department}</span>
                    </div>

                    <div className="last-message-info">
                      <span className="last-message-contents">
                        <b title={email.Title}>
                          {renderLastEmail(email.Title)}
                        </b>{" "}
                        {renderLastEmail(email.LastBodyText)}
                      </span>

                      <span className="message-time">
                        {formatTime(email.LastSentTime)}
                      </span>
                    </div>

                    <div className="agent-campaign-info">
                      {level > 0 && (
                        <>
                          <div className="agent-info">
                            <Icon
                              icon="material-symbols:support-agent"
                              color="#727272"
                              width="18"
                              height="18"
                              style={{ paddingRight: "4px" }}
                            />

                            <p className="agent-name">
                              {email.AssignedTo
                                ? email.AssignedTo
                                : "Not Assigned"}
                            </p>
                          </div>
                        </>
                      )}

                      <div className="email-status">
                        <div>{email.Status}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <span style={{ display: "flex", justifyContent: "center" }}>
                No data found
              </span>
            )}
          </InfiniteScroll>
        )}

        {isAssignAgentModalShown && (
          <AssignAgentModal
            agentToAssign={agentToAssign}
            onClose={() => {
              setIsAssignAgentModalShown(false);
              setAgentToAssign(null);
            }}
            setAgentToAssign={setAgentToAssign}
            setIsAssignAgentWarningShown={setIsAssignAgentWarningShown}
          />
        )}

        <WarningDialog
          confirmAction={assignAgent}
          contents={
            <>
              <p>
                You are about to assign these emails to{" "}
                <span>{agentToAssign?.AgentName}</span>.
              </p>

              <p>Are you sure?</p>
            </>
          }
          isOpen={isAssignAgentWarningShown}
          isProcessing={isAssigning}
          setIsOpen={setIsAssignAgentWarningShown}
        />
      </div>
    </>
  );
};

export default EmailList;
